import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import SectionCard from "./SectionCard";
import RecordFormModal from "./RecordFormModal";
import { api, photoUrl, formatApiError } from "../../api";
import { fmtDate, fmtKm, fmtTry, editTimeLeft } from "../../lib/labels";

const emptyBakim = () => ({ date: new Date().toISOString().slice(0, 10), title: "", part: "", servis: "", cost: "", km: "", description: "", photos: [] });
const emptyHasar = () => ({ date: new Date().toISOString().slice(0, 10), type: "hasar", zone: "", damage_type: "", title: "", description: "", cost: "", km: "", severity: "low", photos: [] });

export default function RecordsSection({ vehicle, kind, title, desc, Icon, testid, onSaved }) {
  const [modal, setModal] = useState(null); // {record|null, data}

  const records = (vehicle.history || [])
    .filter((h) => (kind === "bakim" ? h.type === "servis" : ["kaza", "hasar"].includes(h.type)))
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  const openAdd = () => setModal({ record: null, data: kind === "bakim" ? emptyBakim() : emptyHasar() });
  const openEdit = (r) => setModal({
    record: r,
    data: kind === "bakim"
      ? { date: r.date, title: r.title, part: r.part || "", servis: r.servis || "", cost: r.cost ?? "", km: r.km ?? "", description: r.description || "", photos: r.photos || [] }
      : { date: r.date, type: r.type, zone: r.zone || "", damage_type: r.damage_type || "", title: r.title, description: r.description || "", cost: r.cost ?? "", km: r.km ?? "", severity: r.severity || "low", photos: r.photos || [] },
  });

  const remove = async (r) => {
    if (!window.confirm("Bu kayıt silinsin mi?")) return;
    try {
      const { data } = await api.delete(`/my-vehicles/${vehicle.id}/records/${r.id}`);
      onSaved(data);
      toast.success("Kayıt silindi");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <SectionCard testid={testid} title={title} desc={desc} Icon={Icon} hideActions>
      <button data-testid={`${testid}-add-button`} onClick={openAdd}
        className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition-colors">
        <Plus className="w-3.5 h-3.5" /> Kayıt Ekle
      </button>

      <div className="mt-4 space-y-3">
        {records.length === 0 && (
          <div data-testid={`${testid}-empty`} className="rounded-xl border border-dashed border-white/15 p-8 text-center">
            <Icon className="w-8 h-8 text-slate-600 mx-auto" />
            <p className="mt-3 text-base font-semibold text-slate-300">Henüz kayıt bulunmuyor</p>
            <button data-testid={`${testid}-empty-add-button`} onClick={openAdd}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold px-5 py-2.5 rounded-xl bg-cyan-400 text-slate-950 hover:bg-cyan-300 transition-colors">
              <Plus className="w-4 h-4" /> Kayıt Ekle
            </button>
          </div>
        )}
        {records.map((r) => (
          <div key={r.id} className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-sm text-slate-100">{r.title}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {fmtDate(r.date)}{r.km != null && ` • ${fmtKm(r.km)}`}
                  {kind === "bakim" && r.servis && ` • ${r.servis}`}
                  {kind === "bakim" && r.part && ` • Parça: ${r.part}`}
                  {kind === "hasar" && r.zone && ` • Bölge: ${r.zone}`}
                  {kind === "hasar" && r.damage_type && ` • Tür: ${r.damage_type}`}
                  {fmtTry(r.cost) && ` • ${fmtTry(r.cost)}`}
                </p>
                {r.description && <p className="text-xs text-slate-400 mt-1">{r.description}</p>}
                {r.photos && r.photos.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {r.photos.map((p) => (
                      <img key={`${r.id}-${p}`} src={photoUrl(p)} alt="" className="w-14 h-10 object-cover rounded-lg border border-white/10" />
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0 items-center">
                {r.locked ? (
                  <span data-testid={`${testid}-locked-${r.id}`} className="text-[10px] font-semibold px-2.5 py-1.5 rounded-lg border border-amber-500/30 text-amber-300/90">
                    Kilitli (24 saat doldu)
                  </span>
                ) : (
                  <>
                    {r.created_at && editTimeLeft(r.created_at) && (
                      <span data-testid={`${testid}-countdown-${r.id}`} className="text-[10px] font-semibold px-2.5 py-1.5 rounded-lg border border-cyan-400/30 text-cyan-300/90">
                        Düzenleme: {editTimeLeft(r.created_at)} kaldı
                      </span>
                    )}
                    <button data-testid={`${testid}-edit-${r.id}`} onClick={() => openEdit(r)}
                      className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`${testid}-delete-${r.id}`} onClick={() => remove(r)}
                      className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/40 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <RecordFormModal vehicle={vehicle} kind={kind} modal={modal} setModal={setModal} onSaved={onSaved} testid={testid} />
      )}
    </SectionCard>
  );
}
